import React, { Suspense, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { HelmetProvider } from 'react-helmet-async';
import { I18nextProvider } from 'react-i18next';

import { Toaster } from '@codes/ui-kit/components/ui/toaster';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { Loader } from '@codes/loader';
import { ErrorFallback, LoadingFallback } from '@codes/shared';
import { ThemeProvider } from '@codes/theme';
import { useInitWebCore, useTokenRefresh, useWebCoreStore } from '@codes/web-core';

import { Router } from './routes';
import i18n from '../i18n';

export function App() {
    const queryClient = useMemo(() => {
        return new QueryClient({
            defaultOptions: {
                queries: {
                    staleTime: Infinity,
                    retry: 1,
                },
            },
        });
    }, []);

    const isWebCoreReady = useInitWebCore();
    const { isAuthenticated } = useWebCoreStore();
    const { isInitialized: isTokenInitialized } = useTokenRefresh(isWebCoreReady);
    const canRenderApp = isWebCoreReady && (!isAuthenticated || isTokenInitialized);

    if (!canRenderApp) {
        return <LoadingFallback />;
    }

    return (
        <I18nextProvider i18n={i18n}>
            <Suspense fallback={<LoadingFallback />}>
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <HelmetProvider>
                        <QueryClientProvider client={queryClient}>
                            <ThemeProvider>
                                <Router />
                                <Loader />
                                <Toaster />
                            </ThemeProvider>
                            {process.env.NODE_ENV !== 'prod' && <ReactQueryDevtools />}
                        </QueryClientProvider>
                    </HelmetProvider>
                </ErrorBoundary>
            </Suspense>
        </I18nextProvider>
    );
}

export default App;
